<template>
  <v-container fluid class="pa-0">
    <!--TOOLBAR-->
    <v-toolbar flat color="red-darken-3">
      <v-toolbar-title class="hidden-sm-and-down">
        {{ $t("shippingBulkImport.label") }}
      </v-toolbar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-toolbar>

    <!--ALERT-->
    <v-row v-if="alert.value" justify="center" class="px-4 pt-2">
      <v-col cols="12" xs="12">
        <v-alert
          :type="alert.type"
          :value="alert.value"
          closable
          @click:close="alert.value = false"
        >
          {{ alert.message }}
        </v-alert>
      </v-col>
    </v-row>

    <!--UPLOAD SECTION-->
    <div class="col-12 pa-4">
      <v-card flat>
        <v-toolbar flat theme="dark">
          <v-toolbar-title>
            {{ $t("shippingBulkImport.upload_section") }}
          </v-toolbar-title>
        </v-toolbar>

        <v-container>
          <v-row>
            <!--Institution selector (admins only)-->
            <v-col v-if="showInstitutionSelector" cols="12" xs="12" sm="12" md="4">
              <label class="ml-2">{{
                $t("parts.advanced_search.institution")
              }}</label>
              <v-autocomplete
                v-model="institutionId"
                :items="institutions"
                :loading="loadingInstitutions"
                :label="$t('parts.advanced_search.institution')"
                :item-title="getInstitutionLabel"
                item-value="_id"
                variant="solo"
                color="red-darken-1"
                class="ml-2 mr-2 pt-2"
                clearable
              />
            </v-col>

            <!--Site selector-->
            <v-col cols="12" xs="12" sm="6" md="3">
              <label class="ml-2">{{ $t("shippingBulkImport.site") }}</label>
              <v-select
                v-model="site"
                :items="siteOptions"
                :label="$t('shippingBulkImport.site')"
                item-title="label"
                item-value="value"
                variant="solo"
                color="red-darken-1"
                class="ml-2 mr-2 pt-2"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props" :title="undefined">
                    <template #prepend>
                      <img
                        v-if="item.raw.flag"
                        :src="item.raw.flag"
                        height="22"
                        width="22"
                        class="mr-3"
                      />
                      <v-icon v-else class="mr-3" size="22">mdi-earth</v-icon>
                    </template>
                    {{ item.raw.label }}
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <img
                      v-if="item.raw.flag"
                      :src="item.raw.flag"
                      height="20"
                      width="20"
                      class="mr-2"
                    />
                    <v-icon v-else size="20" class="mr-2">mdi-earth</v-icon>
                    <span>{{ item.raw.label }}</span>
                  </div>
                </template>
              </v-select>
            </v-col>

            <!--File input-->
            <v-col cols="12" xs="12" sm="12" md="5">
              <label class="ml-2">{{
                $t("shippingBulkImport.excel_file")
              }}</label>
              <v-file-input
                v-model="file"
                :label="$t('shippingBulkImport.excel_file_hint')"
                accept=".xlsx,.xls"
                variant="solo"
                prepend-icon="mdi-microsoft-excel"
                color="red-darken-1"
                class="ml-2 mr-2 pt-2"
                :multiple="false"
                clearable
              />
            </v-col>
          </v-row>

          <!--Monthly limit warning (429)-->
          <v-row v-if="monthlyLimitError">
            <v-col cols="12" xs="12">
              <v-alert type="warning" variant="outlined" border="bottom">
                <strong>{{
                  $t("shippingBulkImport.monthly_limit_title")
                }}</strong>
                <div class="mt-1">
                  {{
                    $t("shippingBulkImport.monthly_limit_desc", {
                      site: monthlyLimitError.existingSite,
                      date: formatDate(monthlyLimitError.uploadedAt),
                    })
                  }}
                </div>
                <div v-if="monthlyLimitError.existingImportId" class="mt-2">
                  <v-btn
                    size="small"
                    variant="outlined"
                    color="warning"
                    :loading="
                      downloadingFile === monthlyLimitError.existingImportId
                    "
                    @click="downloadFile(monthlyLimitError.existingImportId)"
                  >
                    <v-icon class="mr-1">mdi-download</v-icon>
                    {{ $t("shippingBulkImport.download_existing") }}
                  </v-btn>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!--Weight range validation error (400)-->
          <v-row v-if="weightRangeValidationError">
            <v-col cols="12" xs="12">
              <v-alert type="error" variant="outlined" border="bottom">
                <strong>{{ weightRangeValidationError.message }}</strong>

                <div
                  v-if="weightRangeValidationError.invalidValues?.length"
                  class="mt-2"
                >
                  <div class="text-subtitle-2">
                    {{ $t("shippingBulkImport.invalid_values") }}:
                    {{ weightRangeValidationError.invalidValues.join(", ") }}
                  </div>
                </div>

                <div
                  v-if="weightRangeValidationError.allowedValues?.length"
                  class="mt-2"
                >
                  <div class="text-subtitle-2">
                    {{ $t("shippingBulkImport.allowed_values") }}:
                    {{
                      formatAllowedWeightRanges(
                        weightRangeValidationError.allowedValues,
                      )
                    }}
                  </div>
                </div>

                <div
                  v-if="weightRangeValidationError.invalidRows?.length"
                  class="mt-3"
                >
                  <v-table density="compact">
                    <thead>
                      <tr>
                        <th>{{ $t("shippingBulkImport.row") }}</th>
                        <th>{{ $t("shippingBulkImport.value") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(
                          invalidRow, idx
                        ) in weightRangeValidationError.invalidRows"
                        :key="`${invalidRow.row}-${idx}`"
                      >
                        <td>{{ invalidRow.row }}</td>
                        <td>{{ invalidRow.value }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!--Upload button-->
          <v-row class="mt-2">
            <v-col>
              <v-btn
                :loading="uploading"
                :disabled="!canUpload"
                color="red-darken-1"
                class="white--text"
                @click="uploadFile"
              >
                <v-icon class="mr-2">mdi-cloud-upload</v-icon>
                {{ $t("shippingBulkImport.upload_button") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </div>

    <!--EBAY CSV DOWNLOAD SECTION (commented out for now)
    <div class="col-12 pa-4">
      <v-card flat>
        <v-toolbar flat theme="dark">
          <v-toolbar-title>
            {{ $t("shippingBulkImport.ebay_csv_section") }}
          </v-toolbar-title>
        </v-toolbar>

        <v-container>
          <v-row>
            <v-col xs="12">
              <v-alert
                :value="true"
                type="info"
                variant="outlined"
                border="bottom"
                class="mb-3"
              >
                {{ $t("shippingBulkImport.ebay_csv_info") }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row v-if="enabledSites.length">
            <v-col
              v-for="s in enabledSites"
              :key="s"
              xs="12"
              sm="6"
              md="3"
              lg="2"
              class="mb-2"
            >
              <v-btn
                color="red-darken-1"
                class="white--text"
                :disabled="!activeInstitutionId || !!downloadingCsv"
                :loading="downloadingCsv === s"
                block
                @click="downloadEbayCsv(s)"
              >
                <img
                  :src="getCountryFlag(s)"
                  height="20"
                  width="20"
                  class="mr-2"
                />
                eBay {{ s.toUpperCase() }}
                <v-icon class="ml-2">mdi-download</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-else-if="activeInstitutionId">
            <v-col xs="12">
              <v-alert type="warning" variant="tonal" density="compact">
                {{ $t("shippingBulkImport.no_ebay_sites") }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col xs="12">
              <v-card variant="outlined" class="pa-4 mb-3" height="auto">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  {{ $t("shippingBulkImport.ebay_upload_guide_title") }}
                </div>
                <ol class="pl-5 mb-3">
                  <li>{{ $t("shippingBulkImport.ebay_upload_step_1") }}</li>
                  <li>{{ $t("shippingBulkImport.ebay_upload_step_2") }}</li>
                  <li>{{ $t("shippingBulkImport.ebay_upload_step_3") }}</li>
                  <li>{{ $t("shippingBulkImport.ebay_upload_step_4") }}</li>
                </ol>
                <v-btn
                  color="red-darken-1"
                  variant="outlined"
                  href="https://www.ebay.it/sh/reports/uploads"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon class="mr-1">mdi-open-in-new</v-icon>
                  {{ $t("shippingBulkImport.open_ebay_uploads") }}
                </v-btn>
                <v-img
                  :src="ebayUploadScreenshot"
                  class="mt-4 rounded border"
                  cover
                />
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </div>
    -->

    <!--IMPORT HISTORY TABLE-->
    <div class="col-12 pa-4">
      <v-card flat>
        <v-toolbar flat theme="dark">
          <v-toolbar-title>
            {{ $t("shippingBulkImport.history_title") }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon variant="text" @click="getImportLogs">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Mobile card list -->
        <div class="d-md-none import-history-mobile-list px-2 py-3">
          <v-progress-linear
            v-if="loadingLogs"
            indeterminate
            color="red"
            class="mb-2"
          />

          <div
            v-for="(item, index) in paginatedLogs"
            :key="item._id || `${item.originalFileName}-${index}`"
            class="import-history-mobile-card"
            :class="{
              'import-history-mobile-card--selected': index === activeHistoryRow,
            }"
            @click="activeHistoryRow = activeHistoryRow === index ? null : index"
          >
            <div class="import-history-mobile-card__header">
              <div class="import-history-mobile-card__title">
                {{ item.originalFileName }}
              </div>
              <div class="import-history-mobile-card__chips">
                <v-chip
                  :color="
                    item.status === 'success' ? 'green-darken-1' : 'red-darken-1'
                  "
                  size="small"
                >
                  {{ item.status }}
                </v-chip>
                <v-chip size="small" variant="outlined">
                  {{ item.site?.toUpperCase() }}
                </v-chip>
              </div>
            </div>

            <div class="import-history-mobile-card__row">
              <span class="import-history-mobile-card__label">
                {{ headers[2].title }}
              </span>
              <span>{{ formatDate(item.uploadedAt) }}</span>
            </div>

            <div v-if="item.uploadedBy" class="import-history-mobile-card__row">
              <span class="import-history-mobile-card__label">
                {{ headers[3].title }}
              </span>
              <span>
                {{ item.uploadedBy.name }} {{ item.uploadedBy.surname }}
              </span>
            </div>

            <div
              v-if="item.summary || item.errorMessage"
              class="import-history-mobile-card__row import-history-mobile-card__row--stack"
            >
              <span class="import-history-mobile-card__label">
                {{ headers[5].title }}
              </span>
              <span
                v-if="item.summary"
                class="text-caption"
              >
                {{ getSummaryText(item.summary) }}
              </span>
              <span
                v-else-if="item.errorMessage"
                class="text-caption text-red-darken-1"
              >
                {{ item.errorMessage }}
              </span>
            </div>

            <div
              v-if="item.status === 'success'"
              class="import-history-mobile-card__actions"
            >
              <v-btn
                size="small"
                variant="outlined"
                color="red-darken-1"
                :loading="downloadingFile === item._id"
                @click.stop="downloadFile(item._id)"
              >
                <v-icon size="small" class="mr-1">mdi-download</v-icon>
                {{ $t("shippingBulkImport.download_file") }}
              </v-btn>
            </div>
          </div>

          <v-alert
            v-if="!loadingLogs && logs.length < 1"
            type="info"
            variant="tonal"
            class="mt-2"
          >
            No data available.
          </v-alert>

          <div v-if="logs.length > 0" class="import-history-mobile-footer pb-2">
            <v-data-table
              v-model:options="logsPagination"
              :headers="headers"
              :items="logs"
              :loading="loadingLogs"
              :items-per-page="logsPagination.itemsPerPage"
              :items-per-page-options="[
                { value: 10, title: '10' },
                { value: 25, title: '25' },
                { value: 50, title: '50' },
              ]"
              items-per-page-text="Rows per page"
              class="elevation-0"
              @update:options="updateLogsPagination"
            />
          </div>
        </div>

        <div class="d-none d-md-block import-history-table-host">
        <v-data-table
          v-model:options="logsPagination"
          :headers="headers"
          :items="logs"
          :loading="loadingLogs"
          :items-per-page="logsPagination.itemsPerPage"
          :items-per-page-options="[
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          class="elevation-1"
          @update:options="updateLogsPagination"
        >
          <!--Status chip-->
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="
                item.status === 'success' ? 'green-darken-1' : 'red-darken-1'
              "
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <!--Site chip-->
          <template #[`item.site`]="{ item }">
            <v-chip size="small" variant="outlined">
              {{ item.site?.toUpperCase() }}
            </v-chip>
          </template>

          <!--Uploaded at-->
          <template #[`item.uploadedAt`]="{ item }">
            {{ formatDate(item.uploadedAt) }}
          </template>

          <!--Uploaded by-->
          <template #[`item.uploadedBy`]="{ item }">
            <span v-if="item.uploadedBy">
              {{ item.uploadedBy.name }} {{ item.uploadedBy.surname }}
            </span>
          </template>

          <!--Summary / error-->
          <template #[`item.summary`]="{ item }">
            <span v-if="item.summary" class="text-caption">
              {{ getSummaryText(item.summary) }}
            </span>
            <span
              v-else-if="item.errorMessage"
              class="text-caption text-red-darken-1"
            >
              {{ item.errorMessage }}
            </span>
          </template>

          <!--Actions-->
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="item.status === 'success'"
              size="small"
              variant="text"
              color="red-darken-1"
              :loading="downloadingFile === item._id"
              @click="downloadFile(item._id)"
            >
              <v-icon size="small" class="mr-1">mdi-download</v-icon>
              {{ $t("shippingBulkImport.download_file") }}
            </v-btn>
          </template>
        </v-data-table>
        </div>
      </v-card>
    </div>

    <!--SUCCESS DIALOG-->
    <v-dialog v-model="successDialog" max-width="650">
      <v-card>
        <v-card-title class="redColor text-white pa-4">
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          {{ $t("shippingBulkImport.success_title") }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="importSummary">
            <!--Weight range summary-->
            <div v-if="importSummary.weightRange" class="mb-3">
              <div class="text-subtitle-2 font-weight-bold mb-1">
                Weight Range
              </div>
              <div class="text-body-2">
                Reference Costs: inserted
                <strong>{{
                  importSummary.weightRange.referenceCosts?.inserted ?? 0
                }}</strong
                >, updated
                <strong>{{
                  importSummary.weightRange.referenceCosts?.updated ?? 0
                }}</strong>
                &nbsp;|&nbsp; Parts modified:
                <strong>{{
                  importSummary.weightRange.parts?.modified ?? 0
                }}</strong>
              </div>
            </div>

            <!--Per-site shipping costs-->
            <div v-if="importSummary.shippingCosts">
              <div
                v-for="(siteData, siteCode) in importSummary.shippingCosts"
                :key="siteCode"
                class="mb-3"
              >
                <div class="text-subtitle-2 font-weight-bold mb-1">
                  Shipping Costs — {{ siteCode.toUpperCase() }}
                </div>
                <div class="text-body-2">
                  Reference Costs: inserted
                  <strong>{{ siteData.referenceCosts?.inserted ?? 0 }}</strong
                  >, updated
                  <strong>{{ siteData.referenceCosts?.updated ?? 0 }}</strong>
                  &nbsp;|&nbsp; Parts modified:
                  <strong>{{ siteData.parts?.modified ?? 0 }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!--Download imported file-->
          <div v-if="lastImportLogId" class="mt-3">
            <v-btn
              size="small"
              color="red-darken-1"
              variant="outlined"
              :loading="downloadingFile === lastImportLogId"
              @click="downloadFile(lastImportLogId)"
            >
              <v-icon class="mr-1">mdi-download</v-icon>
              {{ $t("shippingBulkImport.download_imported_file") }}
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red-darken-1"
            variant="text"
            @click="successDialog = false"
          >
            {{ $t("shippingBulkImport.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

import ebayUploadScreenshot from "@/assets/UploadEbayCsv.png";
import getRequest from "../api/getRequest";
import postRequest from "../api/postRequest";
import config from "../config";

export default {
  data: () => ({
    ebayUploadScreenshot,
    alert: { value: false, type: "error", message: "" },
    // Upload form
    institutionId: null,
    institutions: [],
    loadingInstitutions: false,
    site: "all",
    file: null,
    uploading: false,
    monthlyLimitError: null,
    weightRangeValidationError: null,
    // Success
    successDialog: false,
    importSummary: null,
    lastImportLogId: null,
    // Selected institution object (to read eBay site flags)
    selectedInstitution: null,
    // History table
    logs: [],
    loadingLogs: false,
    activeHistoryRow: null,
    logsPagination: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [{ key: "uploadedAt", order: "desc" }],
      sortDesc: [true],
    },
    downloadingFile: null,
    downloadingCsv: null,
    headers: [
      { title: "File Name", key: "originalFileName", sortable: false },
      { title: "Site", key: "site", sortable: false },
      { title: "Uploaded At", key: "uploadedAt", sortable: true },
      { title: "Uploaded By", key: "uploadedBy", sortable: false },
      { title: "Status", key: "status", sortable: false },
      { title: "Summary / Error", key: "summary", sortable: false },
      { title: "Actions", key: "actions", sortable: false },
    ],
  }),

  computed: {
    showInstitutionSelector() {
      const role = this.$store.getters.role;
      return ["SuperAdmin", "LekoAdmin", "LekoEmployee"].includes(role);
    },
    activeInstitutionId() {
      if (this.showInstitutionSelector) return this.institutionId;
      return this.$store.getters.institutionId;
    },
    canUpload() {
      return !!(this.activeInstitutionId && this.site && this.file);
    },
    enabledSites() {
      if (this.showInstitutionSelector) {
        if (!this.selectedInstitution) return [];
        return this.getSitesFromInstitution(this.selectedInstitution);
      }
      // Non-admin: read from Vuex store (populated by verifyToken)
      const sites = [];
      if (this.$store.getters.institutionExportEbayLeko) sites.push("it");
      const intl = this.$store.getters.institutionEbayInternational;
      if (intl) {
        ["de", "fr", "es"].forEach((s) => {
          if (intl[s]) sites.push(s);
        });
      }
      return sites;
    },
    sortedLogs() {
      const items = [...(this.logs || [])];
      const sortBy = this.logsPagination.sortBy?.[0];
      if (sortBy?.key === "uploadedAt") {
        items.sort((a, b) => {
          const da = new Date(a?.uploadedAt || 0).getTime();
          const db = new Date(b?.uploadedAt || 0).getTime();
          return sortBy.order === "desc" ? db - da : da - db;
        });
      }
      return items;
    },
    paginatedLogs() {
      const page = this.logsPagination.page || 1;
      const perPage = this.logsPagination.itemsPerPage || 10;
      const start = (page - 1) * perPage;
      return this.sortedLogs.slice(start, start + perPage);
    },
    siteOptions() {
      const siteMap = {
        it: {
          label: "Italy (it)",
          flag: "https://portal.lekotech.com/img/icons/ebayIcons/italy.svg",
        },
        de: {
          label: "Germany (de)",
          flag: "https://portal.lekotech.com/img/icons/ebayIcons/germany.svg",
        },
        fr: {
          label: "France (fr)",
          flag: "https://portal.lekotech.com/img/icons/ebayIcons/france.svg",
        },
        es: {
          label: "Spain (es)",
          flag: "https://portal.lekotech.com/img/icons/ebayIcons/spain.svg",
        },
      };
      const options = this.enabledSites.map((s) => ({
        value: s,
        label: siteMap[s]?.label || s.toUpperCase(),
        flag: siteMap[s]?.flag || null,
      }));
      if (options.length > 1) {
        options.push({ value: "all", label: "All sites", flag: null });
      }
      return options;
    },
  },

  watch: {
    institutionId(val) {
      this.monthlyLimitError = null;
      this.weightRangeValidationError = null;
      this.selectedInstitution = val
        ? this.institutions.find((i) => i._id === val) || null
        : null;
      if (val) this.getImportLogs();
    },
    enabledSites(sites) {
      if (sites.length > 1) {
        this.site = "all";
      } else if (sites.length === 1) {
        this.site = sites[0];
      } else {
        this.site = null;
      }
    },
  },

  created() {
    if (this.showInstitutionSelector) {
      this.getInstitutions();
    } else {
      // Set default site based on what's enabled for this institution
      const sites = this.enabledSites;
      this.site = sites.length > 1 ? "all" : sites[0] || null;
      this.getImportLogs();
    }
  },

  methods: {
    updateLogsPagination(options) {
      this.logsPagination = {
        ...this.logsPagination,
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 10,
        page: options.page ? options.page : 1,
      };
      this.activeHistoryRow = null;
    },
    getInstitutionLabel(institution) {
      if (!institution || typeof institution !== "object") return "";
      return (
        institution.title ||
        institution.name ||
        institution.institutionName ||
        institution.businessName ||
        institution.email ||
        institution._id
      );
    },

    getInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions || [];
        })
        .catch(() => {})
        .finally(() => {
          this.loadingInstitutions = false;
        });
    },

    getImportLogs() {
      if (!this.activeInstitutionId) return;
      this.loadingLogs = true;
      getRequest(
        "/shipping-bulk-import/import-logs",
        `institutionId=${this.activeInstitutionId}`,
      )
        .then((data) => {
          this.logs = Array.isArray(data) ? data : [];
          this.logsPagination.page = 1;
          this.activeHistoryRow = null;
        })
        .catch(() => {
          this.logs = [];
        })
        .finally(() => {
          this.loadingLogs = false;
        });
    },

    async uploadFile() {
      if (!this.canUpload) return;
      this.uploading = true;
      this.monthlyLimitError = null;
      this.weightRangeValidationError = null;
      this.alert = { value: false, type: "error", message: "" };

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("institutionId", this.activeInstitutionId);
      formData.append("site", this.site);

      try {
        const response = await postRequest.formData(
          "/shipping-bulk-import",
          "",
          formData,
        );
        const { logId, summary } = response.data;
        this.lastImportLogId = logId;
        this.importSummary = summary;
        this.successDialog = true;
        this.file = null;
        this.getImportLogs();
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        if (status === 429) {
          this.monthlyLimitError = data;
        } else if (
          status === 400 &&
          data &&
          typeof data === "object" &&
          data.message === "Some weightRange values are invalid."
        ) {
          this.weightRangeValidationError = {
            message:
              this.$t("shippingBulkImport.invalid_weight_range_message") ||
              data.message,
            allowedValues: Array.isArray(data.allowedValues)
              ? data.allowedValues
              : [],
            invalidValues: Array.isArray(data.invalidValues)
              ? data.invalidValues
              : [],
            invalidRows: Array.isArray(data.invalidRows)
              ? data.invalidRows
              : [],
          };
        } else {
          const message =
            (typeof data === "string" ? data : data?.message) ||
            this.$t("shippingBulkImport.upload_failed");
          this.alert = { value: true, type: "error", message };
        }
      } finally {
        this.uploading = false;
      }
    },

    async downloadFile(logId) {
      const { token } = localStorage;
      const serverUri = config.getServerUri();
      this.downloadingFile = logId;
      try {
        const response = await axios.get(
          `${serverUri}/shipping-bulk-import/import-logs/${logId}/file`,
          {
            headers: { authorization: token },
            responseType: "blob",
          },
        );
        const url = URL.createObjectURL(response.data);
        const cd = response.headers["content-disposition"];
        const match = cd && cd.match(/filename="?([^";\n]+)"?/);
        const filename = match ? match[1] : "import.xlsx";
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.alert = {
          value: true,
          type: "error",
          message: this.$t("shippingBulkImport.download_failed"),
        };
      } finally {
        this.downloadingFile = null;
      }
    },

    async downloadEbayCsv(s) {
      if (!this.activeInstitutionId) return;
      const { token } = localStorage;
      const serverUri = config.getServerUri();
      this.downloadingCsv = s;
      try {
        const response = await axios.get(
          `${serverUri}/shipping-bulk-import/ebay-csv`,
          {
            headers: { authorization: token },
            params: { institutionId: this.activeInstitutionId, site: s },
            responseType: "blob",
          },
        );
        const url = URL.createObjectURL(response.data);
        const cd = response.headers["content-disposition"];
        const match = cd && cd.match(/filename="?([^";\n]+)"?/);
        const filename = match ? match[1] : `ebay-${s}.csv`;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.alert = {
          value: true,
          type: "error",
          message: `${this.$t("shippingBulkImport.download_csv_failed")} ${s.toUpperCase()}.`,
        };
      } finally {
        this.downloadingCsv = null;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString();
    },

    formatAllowedWeightRanges(values) {
      return values
        .map((value) =>
          value === null ? this.$t("shippingBulkImport.empty") : value,
        )
        .join(", ");
    },

    getSummaryText(summary) {
      if (!summary) return "";
      const parts = [];
      if (summary.weightRange?.parts?.modified != null) {
        parts.push(`${summary.weightRange.parts.modified} parts (weight)`);
      }
      if (summary.shippingCosts) {
        for (const [siteCode, data] of Object.entries(summary.shippingCosts)) {
          if (data.parts?.modified != null) {
            parts.push(`${data.parts.modified} parts (${siteCode})`);
          }
        }
      }
      return parts.join(", ");
    },

    getSitesFromInstitution(institution) {
      const sites = [];
      if (institution.export_ebay_leko) sites.push("it");
      const intl = institution.exportEbayLekoInternational;
      if (intl) {
        ["de", "fr", "es"].forEach((s) => {
          if (intl[s]) sites.push(s);
        });
      }
      return sites;
    },

    getCountryFlag(s) {
      const flags = {
        it: "https://portal.lekotech.com/img/icons/ebayIcons/italy.svg",
        de: "https://portal.lekotech.com/img/icons/ebayIcons/germany.svg",
        fr: "https://portal.lekotech.com/img/icons/ebayIcons/france.svg",
        es: "https://portal.lekotech.com/img/icons/ebayIcons/spain.svg",
      };
      return flags[s] || "";
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .import-history-mobile-footer :deep(.v-table__wrapper),
  .import-history-mobile-footer :deep(.v-data-table__wrapper),
  .import-history-mobile-footer :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .import-history-mobile-footer :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}

.import-history-mobile-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
.import-history-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.import-history-mobile-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.import-history-mobile-card__title {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.35;
  word-break: break-word;
}
.import-history-mobile-card__chips {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}
.import-history-mobile-card__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.8rem;
  margin-bottom: 6px;
}
.import-history-mobile-card__row--stack {
  flex-direction: column;
  align-items: stretch;
}
.import-history-mobile-card__label {
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
  flex-shrink: 0;
}
.import-history-mobile-card__actions {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.import-history-mobile-card__actions .v-btn {
  width: 100%;
}
</style>
